import { motion } from "framer-motion";
import { HeartHandshake } from "lucide-react";

function formatRelativeDate(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
    } catch {
        return "";
    }
}

/**
 * Iter-27.63 — Messages de remerciement émotionnels.
 * Carte de gratitude (lettre) avec citation grande, signature, vidéo selfie.
 */
export default function GratitudeMessages({ items, association }) {
    if (!items || items.length === 0) {
        return (
            <p className="text-white/40 text-sm py-6 text-center">
                Aucun message de remerciement pour le moment. Reviens plus tard !
            </p>
        );
    }
    return (
        <div className="space-y-5">
            <p className="text-sm text-white/65 font-light leading-relaxed">
                Voici des messages personnels de{" "}
                <span className="text-shariti-yellow font-bold">{association.name}</span> à toi et
                à tous les ambassadeurs qui portent leur cause sur le terrain.
            </p>
            {items.map((g, idx) => {
                const accent = association.primary_color || "#FCDB53";
                return (
                    <motion.article
                        key={g.news_id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: idx * 0.06 }}
                        className="relative rounded-3xl overflow-hidden"
                        style={{
                            background: `linear-gradient(135deg, ${accent}1A 0%, rgba(255,255,255,0.03) 100%)`,
                            border: `1px solid ${accent}55`,
                        }}
                        data-testid={`gratitude-card-${g.news_id}`}
                    >
                        <div className="p-6 sm:p-8">
                            <div className="flex items-center gap-2 mb-4">
                                <HeartHandshake className="w-5 h-5" style={{ color: accent }} />
                                <span
                                    className="text-[10px] font-extrabold uppercase tracking-[0.25em]"
                                    style={{ color: accent }}
                                >
                                    Message à toi
                                </span>
                            </div>
                            <h3 className="text-xl sm:text-2xl font-extrabold text-white mb-3 leading-tight">
                                {g.title}
                            </h3>
                            <p className="text-sm sm:text-base text-white/80 font-light leading-relaxed whitespace-pre-line">
                                {g.body || g.summary}
                            </p>
                            {g.image_url && (
                                <div className="mt-5 mx-auto max-w-sm rounded-2xl overflow-hidden bg-black/40">
                                    {/\.(mp4|webm|mov)(\?|$)/i.test(g.image_url) ? (
                                        <video
                                            src={g.image_url}
                                            controls
                                            playsInline
                                            preload="metadata"
                                            className="w-full max-h-[420px] object-contain bg-black"
                                        />
                                    ) : (
                                        <img
                                            src={g.image_url}
                                            alt=""
                                            className="w-full max-h-[420px] object-contain"
                                        />
                                    )}
                                </div>
                            )}
                            <div className="mt-5 flex items-center gap-3 text-[11px] text-white/45 font-light">
                                <span>— L'équipe {association.name}</span>
                                <span>·</span>
                                <span>{formatRelativeDate(g.created_at)}</span>
                            </div>
                        </div>
                    </motion.article>
                );
            })}
        </div>
    );
}
